import { useMemo } from 'react';

import type { ISubscriber } from '@/Structures/subscribers/types';
import type { Unit } from '@/Units/types';
import { naturalCompare } from '@/util';

/**
 * Filter to billable units (no COMMON_AREA / NON_BILLABLE flags) sorted by natural
 * compare on the SimpleSub name. Pure function for testability.
 */
export function getBillableUnits(units: Unit[]): Unit[] {
  return units
    .filter((u) => {
      const flags = (u.unit_flags ?? []) as string[];
      return !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
    })
    .sort((a, b) => naturalCompare(a.name, b.name));
}

/** Hook wrapper around getBillableUnits with referential stability. Used by every
 * PMS matching dialog / config form. */
export function useBillableUnits(units: Unit[]): Unit[] {
  return useMemo(() => getBillableUnits(units), [units]);
}

/**
 * Result of an auto-match pass over PMS provider rows against SimpleSub units.
 * Caller decides whether to surface a toast based on `count` vs. `total`, and may
 * use `fuzzyMatchedUnitIds` to flag rows the user should double-check.
 */
export interface AutoMatchResult<TRow> {
  matches: Record<string, TRow>;
  count: number;
  total: number;
  /** Unit IDs whose match came from the fuzzy (normalized) pass, not exact or
   * persisted-ID. Empty when `fuzzy: false`. Surface in the UI so users can verify. */
  fuzzyMatchedUnitIds: string[];
}

export interface AutoMatchOptions<TRow> {
  /** Extract the provider-side unique ID from a row (number for RM, string for AppFolio). */
  getRowId: (row: TRow) => string | number;
  /** Extract the provider-side display name from a row (for the name-match fallback). */
  getRowName: (row: TRow) => string;
  /** Optional first-pass: if a SimpleSub unit has a persisted provider ID and that same
   * ID is in the freshly loaded row set, prefer it over a name match. Enables stable
   * mappings across re-uploads (AppFolio uses this; RM does not because RM's matching
   * dialog always re-fetches from the API and doesn't carry persisted IDs forward). */
  getUnitPersistedId?: (unit: Unit) => string | number | null | undefined;
  /** Override which SimpleSub-side name string to compare. Default: `unit.name`. AppFolio
   * uses `pms_config.unit_name ?? unit.name` so previously-customized mappings persist. */
  getUnitName?: (unit: Unit) => string;
  /** When true, enable a final fuzzy pass over still-unmatched units. Fuzzy normalization
   * strips everything that isn't a letter or digit, so "Apt 1" matches "Apt-1" / "Apt #1"
   * but NOT "Apt 11". Fuzzy can never claim a row that exact match would have wanted
   * because exact runs to completion across all units before fuzzy starts. Default: false. */
  fuzzy?: boolean;
}

/** Aggressive normalization for the fuzzy pass: lowercase + strip everything that
 * isn't a letter or digit. Conservative enough that numbered units stay distinct. */
function fuzzyNormalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * Generic auto-match for any PMS provider's row shape. Single source of truth for the
 * matching algorithm; changes here propagate to every provider.
 *
 * Phased algorithm (each phase runs to completion before the next):
 *  1. **Persisted-ID phase** (only if `getUnitPersistedId` is provided): each unit
 *     gets first dibs at the row whose ID equals its stored ID (string-coerced).
 *  2. **Exact-name phase**: every still-unmatched unit gets a shot at exact
 *     case-insensitive name match against an unclaimed row.
 *  3. **Fuzzy-name phase** (only if `fuzzy: true`): remaining unmatched units try a
 *     normalized (alphanumeric-only) name match. Cannot claim a row that exact match
 *     wanted, because exact already finished claiming.
 *
 * Provider-row IDs are deduplicated across the result. Caller surfaces a toast and
 * optional fuzzy-match UI hints based on the returned counts and ID list.
 */
export function autoMatchUnits<TRow>(
  units: Unit[],
  rows: TRow[],
  options: AutoMatchOptions<TRow>,
): AutoMatchResult<TRow> {
  const { getRowId, getRowName, getUnitPersistedId, getUnitName, fuzzy } = options;
  const nameFor = getUnitName ?? ((u: Unit) => u.name);
  const matches: Record<string, TRow> = {};
  const usedRowIds = new Set<string>();
  const fuzzyMatchedUnitIds: string[] = [];

  // Phase 1: persisted-ID match (if enabled)
  let pendingUnits: Unit[];
  if (getUnitPersistedId) {
    pendingUnits = [];
    for (const unit of units) {
      const persisted = getUnitPersistedId(unit);
      if (persisted == null) {
        pendingUnits.push(unit);
        continue;
      }
      const persistedKey = String(persisted);
      const byId = rows.find(
        (r) => String(getRowId(r)) === persistedKey && !usedRowIds.has(String(getRowId(r))),
      );
      if (byId) {
        matches[unit.id] = byId;
        usedRowIds.add(String(getRowId(byId)));
      } else {
        pendingUnits.push(unit);
      }
    }
  } else {
    pendingUnits = [...units];
  }

  // Phase 2: exact name match
  const stillPending: Unit[] = [];
  for (const unit of pendingUnits) {
    const target = (nameFor(unit) ?? '').toLowerCase();
    if (!target) continue;
    const byName = rows.find(
      (r) => getRowName(r).toLowerCase() === target && !usedRowIds.has(String(getRowId(r))),
    );
    if (byName) {
      matches[unit.id] = byName;
      usedRowIds.add(String(getRowId(byName)));
    } else {
      stillPending.push(unit);
    }
  }

  // Phase 3: fuzzy match (opt-in)
  if (fuzzy) {
    for (const unit of stillPending) {
      const target = fuzzyNormalize(nameFor(unit) ?? '');
      if (!target) continue;
      const byFuzzy = rows.find(
        (r) => fuzzyNormalize(getRowName(r)) === target && !usedRowIds.has(String(getRowId(r))),
      );
      if (byFuzzy) {
        matches[unit.id] = byFuzzy;
        usedRowIds.add(String(getRowId(byFuzzy)));
        fuzzyMatchedUnitIds.push(unit.id);
      }
    }
  }

  return {
    matches,
    count: Object.keys(matches).length,
    total: units.length,
    fuzzyMatchedUnitIds,
  };
}

/**
 * Build a lookup map of unit ID → PMS unit name from the units array.
 * Only includes units that have a pms_config.unit_name set.
 */
export function buildUnitPmsNames(units: Unit[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const u of units) {
    if (u.pms_config?.unit_name) {
      map[u.id] = u.pms_config.unit_name;
    }
  }
  return map;
}

/**
 * Build a lookup map of unit ID → PMS unit_id from the units array.
 * RM uses integer UnitIDs; AppFolio uses string IDs (may include a `#` prefix).
 * Only includes units that have a pms_config.unit_id set.
 */
export function buildUnitPmsIds(units: Unit[]): Record<string, number | string> {
  const map: Record<string, number | string> = {};
  for (const u of units) {
    if (u.pms_config?.unit_id != null) {
      map[u.id] = u.pms_config.unit_id;
    }
  }
  return map;
}

/**
 * AppFolio-only: build a lookup map of unit ID → Occupancy UID from the units array.
 * Source is each unit's persisted `pms_config.occupancy_uid` (captured at matching time
 * from the AppFolio Bulk Charges template).
 */
export function buildUnitAppfolioOccupancyUids(units: Unit[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const u of units) {
    if (u.pms_config?.occupancy_uid) {
      map[u.id] = u.pms_config.occupancy_uid;
    }
  }
  return map;
}

/**
 * AppFolio-only: build a lookup map of unit ID → Occupancy ID (the human-readable
 * occupancy number, distinct from Occupancy UID).
 */
export function buildUnitAppfolioOccupancyIds(units: Unit[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const u of units) {
    if (u.pms_config?.occupancy_id) {
      map[u.id] = u.pms_config.occupancy_id;
    }
  }
  return map;
}

/**
 * AppFolio-only: build a lookup map of unit ID → Tenant Name carried from the template.
 */
export function buildUnitAppfolioTenantNames(units: Unit[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const u of units) {
    if (u.pms_config?.tenant_name) {
      map[u.id] = u.pms_config.tenant_name;
    }
  }
  return map;
}

/**
 * Pick the most-likely resident for each unit, formatted as "First Last".
 * Ranking per unit subscription:
 *   1. is_billing_contact == true wins
 *   2. earliest move_in_date (lexicographic ISO compare; nulls sort last)
 *   3. stable order from the subscribers array (i.e. first added)
 * Skips subscribers whose move_out_date is set and falls on/before `asOfEpochSeconds`
 * (a moved-out tenant shouldn't be the billing contact for a current bill).
 *
 * Returns Record<unit_id, "First Last">. Units with no matching subscriber are absent
 * from the map; callers can fall back to empty string.
 */
export function buildUnitResidentNames(
  subscribers: ISubscriber[],
  asOfEpochSeconds?: number,
): Record<string, string> {
  type Candidate = {
    name: string;
    isBillingContact: boolean;
    moveInDate: string | null;
    order: number;
  };
  const byUnit: Record<string, Candidate[]> = {};
  let order = 0;
  for (const sub of subscribers) {
    const name = `${sub.first_name ?? ''} ${sub.last_name ?? ''}`.trim();
    if (!name) continue;
    for (const us of sub.unit_subscriptions ?? []) {
      if (asOfEpochSeconds != null && us.move_out_date) {
        const moveOut = Math.floor(new Date(us.move_out_date).getTime() / 1000);
        if (Number.isFinite(moveOut) && moveOut <= asOfEpochSeconds) continue;
      }
      (byUnit[us.unit_id] ??= []).push({
        name,
        isBillingContact: us.is_billing_contact === true,
        moveInDate: us.move_in_date ?? null,
        order: order++,
      });
    }
  }
  const out: Record<string, string> = {};
  for (const [unitId, cands] of Object.entries(byUnit)) {
    cands.sort((a, b) => {
      if (a.isBillingContact !== b.isBillingContact) return a.isBillingContact ? -1 : 1;
      const aDate = a.moveInDate ?? '￿';
      const bDate = b.moveInDate ?? '￿';
      if (aDate !== bDate) return aDate < bDate ? -1 : 1;
      return a.order - b.order;
    });
    out[unitId] = cands[0].name;
  }
  return out;
}
